import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PrintControlDataCommandHandler } from '../../../accounting/service/command/print-control-data-command-handler.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ControlDataExportForm } from '../../model/control-data/control-data-export-form';
import { ControlDataForm } from '../../model/control-data/control-data-form';
import { ControlDataExportCommandHandlerResolver } from '../../service/command/Control-data-export-command-handler-resolver';
import { CompanyService } from '../../service/data/company.service';
import { ControlDataExportFormService } from '../../service/data/control-data-export-form.service';
import { dateTimeString } from '../../../core/helper/date-time';
import { ASTRAL_MAX_YEAR } from '../../../core/component/astral-components/astral-date-validation';

const MIN_FOUR_DIGIT_YEAR = 1000;

@Component({
    selector: 'control-data-export-form',
    templateUrl: './control-data-export-form.component.html',
    styleUrls: ['./control-data-export-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        NavigationService,
        PrintControlDataCommandHandler,
        ControlDataExportCommandHandlerResolver,
        { provide: DataService, useExisting: ControlDataExportFormService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ControlDataExportFormComponent extends FormComponentBase<ControlDataExportForm> implements OnDestroy {
  constructor(
    private _cf: FormControlExFactory,
    private fb: FormBuilder,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    handlerResolver: ControlDataExportCommandHandlerResolver,
    private _dataService: ControlDataExportFormService,
    messageService: MessageService,
    navigation: NavigationService,
    companyService: CompanyService,
    private columnFactory: ColumnDefinitionFactory,
    private _nativeWindow: NativeWindowService
  ) {
    super(activatedRoute, _dataService, messageService, navigation, dispatcher, handlerResolver);
    this.companyService = companyService;
    this.controlDataColumnDefinition = [
      this.columnFactory.create('CONTROL_DATA.FORM_NUMBER', 'formNumber', ColumnVisibility.mandatory),
      this.columnFactory.create(
        'COOPERATIVE_HOUSING.KU55.FK.630_BOSTRATTBETECKNING',
        'kU55Content.fK630_BostRattBeteckning',
        ColumnVisibility.mandatory
      ),
      this.columnFactory.createDate(
        'COOPERATIVE_HOUSING.KU55.FK.631_OVERLATELSEDATUM',
        'kU55Content.fK631_Overlatelsedatum',
        ColumnVisibility.default
      ),
      this.columnFactory.create('COOPERATIVE_HOUSING.KU55.FK.216_FORNAMN', 'kU55Content.fK216_Fornamn', ColumnVisibility.default),
      this.columnFactory.create('COOPERATIVE_HOUSING.KU55.FK.217_EFTERNAMN', 'kU55Content.fK217_Efternamn', ColumnVisibility.default),
      this.columnFactory.create('COOPERATIVE_HOUSING.KU55.FK.226_ORGNAMN', 'kU55Content.fK226_OrgNamn', ColumnVisibility.default),
      this.columnFactory.createMoney(
        'COOPERATIVE_HOUSING.KU55.FK.643_FORVARVSPRIS',
        'kU55Content.fK643_Forvarvspris',
        ColumnVisibility.default
      ),
      this.columnFactory.createBoolean('COOPERATIVE_HOUSING.KU55.FK.205_BORTTAG', 'kU55Content.fK205_Borttag', ColumnVisibility.optional),
      this.columnFactory.createBoolean('COOPERATIVE_HOUSING.KU55.FK.210_RATTELSE', 'kU55Content.fK210_Rattelse', ColumnVisibility.optional),
    ];
    //this.menuDefinition = [
    //  new MenuItemDefinition('COOPERATIVE_HOUSING.CREATE_NEW_CONTROL_DATA_FILE', ControlDataExportCommand.generateNewControlDataFile)
    //];
  }

  onControlDataRowClicked(e: { context: ControlDataForm; event: any }): void {
    this.navigation.navigate(['cd-55', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  protected createModel(): ControlDataExportForm {
    let model = new ControlDataExportForm();
    model.incomeYear = new Date().getFullYear() - 1;
    return model;
  }

  companyService: CompanyService;

  protected getFormGroup(): FormGroup {
    const form = this.fb.group({
      id: this._cf.createHidden(this.dataContext.id),
      companyId: this._cf.createWithOptions('COMMON.COMPANY', null, { required: this.isNew }),
      companyName: this._cf.createReadonly('COMMON.COMPANY', this.dataContext.companyName),
      incomeYear: this._cf.createWithOptions('COMMON.INCOME_YEAR', this.dataContext.incomeYear, {
        isReadonly: !this.isNew,
        minValue: MIN_FOUR_DIGIT_YEAR,
        maxValue: ASTRAL_MAX_YEAR,
        required: true,
      }),
      createdAt: this._cf.createReadonly(
        'CONTROL_DATA.EXPORT_CREATED_AT',
        dateTimeString(this.dataContext.createdAt)
      ),
      comment: this._cf.create('COMMON.NOTE', this.dataContext.comment),
      files: this.getFileForms(),
    });
    return form;
  }

  files: FormArray;

  private getFileForms(): FormArray {
    this.files = this.fb.array([]);
    for (let file of this.dataContext.files) {
      let form = this.fb.group({
        id: this._cf.createHidden(file.id),
        comment: this._cf.create('COMMON.NOTE', file.comment),
        caption: this._cf.createHidden(file.caption),
        sentToTaxAuthorityAt: this._cf.create('CONTROL_DATA.SENT_AT', file.sentToTaxAuthorityAt),
        // Remove "voided at" from the model entierly?
        voidedAt: this._cf.createHidden(file.voidedAt),
        createdAt: this._cf.createReadonly('CONTROL_DATA.EXPORT_CREATED_AT', dateTimeString(file.createdAt)),
        //forms: this.createFormForms(file.forms)
      });
      this.files.push(form);
    }
    return this.files;
  }

  downloading: boolean = false;

  download(id: string) {
    if (this.downloading) {
      return;
    }
    this.downloading = true;
    this.AddSubscription(
      this._dataService.download(id).subscribe(res => {
        this._nativeWindow.save(res);
        this.downloading = false;
      })
    );
  }

  controlDataColumnDefinition: ColumnDefinition[];
}
